<template>
	<Modals
		:title="'Add a 3P Vendor'"
		:display="display"
		@show="cancelAction"
		:parentTag="'form'"
		:novalidate="'novalidate'"
		:fullMobile="true"
		data-test="modalForAddVendor"
	>
		<template #modal-content>
			<div class="add-vendor">
				<div class="add-vendor__fields">
					<Input
						className="field__input"
						label="3P Vendor Name"
						name="integratorName"
						:required="true"
						v-model="vendorModal.integratorName"
					/>
					<Input
						className="field__input"
						label="Alias"
						v-model="vendorModal.alias"
					/>
					<DatePicker
						v-model="datePartnered"
						name="datePartnered"
						labelText="Partnered with VUE Since *"
						datePickerType="singleDate"
						data-testid="datePartnered"
					/>

					<!-- DEV NOTE: Please review SegmentedControl component, if you select one, the other in the front deselect.-->

					<div class="add-vendor__fields-integration-solution">
						<SegmentedControl
							v-model="vendorModal.dmsis"
							name="Is3PVendorIS"
							:options="is3PVendorISOptions"
							labelText="Is this 3P Vendor an Integration Solution? *"
							data-testid="Is3PVendorIS"
						>
						</SegmentedControl>

						<SegmentedControl
							name="VendorStatus"
							v-model="vendorModal.active"
							:options="vendorStatusOptions"
							labelText="Status *"
						>
						</SegmentedControl>
					</div>

					<div class="add-vendor__fields-support-contact-title">
						Support Contact Info
					</div>

					<Input
						className="field__input"
						label="Contact Name"
						name="contactname"
						:required="true"
						v-model="vendorContact.name"
					/>

					<Input
						className="field__input"
						label="Title"
						name="title"
						v-model="vendorContact.title"
					/>

					<div class="add-vendor__fields-contact-method">
						<div class="add-vendor__fields-contact-method-title">
							Contact Method *
						</div>
						<div class="add-vendor__fields-contact-method-subtitle">
							At least one is required
						</div>
					</div>

					<Input
						className="field__input"
						label="Email"
						inputType="Email"
						name="email"
						placeholder="example@mail.com"
						v-model="vendorContact.email"
						@hasError="handleContactMethodError"
					/>
					<Input
						class="add-vendor__fields-phone"
						className="field__input"
						label="Phone"
						name="phoneNumber"
						inputType="numeric"
						placeholder="(123) 456-7890"
						v-model="vendorContact.phone"
						:minLength="10"
						:maxLength="11"
						@hasError="handleContactMethodError"
					/>
				</div>
			</div>

			<Modals
				title="The vendor name and/or alias entered matches existing records"
				:display="displayDuplicatesModal"
				type="confirmation"
				data-test="modalDuplicates"
				:labelConfirmButton="'Continue'"
				:labelCancelButton="'Back'"
				@show="displayDuplicatesModal = false"
				@close="cancelSecondModal"
				@confirm="continueBtn"
			>
				<template #modal-content>
					<p>Do you want to use one of these:</p>
					<List
						:listItems="possiblesMatches"
						data-test="listPosiblesDuplicates"
					>
						<template #default="slotProps">
							<div class="duplicate-card">
								<label
									:for="
										'possiblesDuplicates' +
											slotProps.item.id
									"
								>
									<div class="add-vendor_duplicate-card">
										<p>
											{{ slotProps.item.integratorName }}
										</p>
										<p>{{ slotProps.item.alias }}</p>
										<p>{{ slotProps.item.website }}</p>
									</div>
								</label>
								<Radiobutton
									name="possiblesDuplicates"
									:id="
										'possiblesDuplicates' +
											slotProps.item.id
									"
									:value="slotProps.item.id"
									:isActive="
										slotProps.item.id === matchIdSelection
									"
									@input="updateMatchIdSelection"
								/>
							</div>
						</template>
					</List>
					<div class="duplicates-vendor-modal__new-record">
						<Radiobutton
							name="newVendorRecord"
							labelText="Continue creating a new vendor record with the information I entered."
							:value="-1"
							@input="updateMatchIdSelection"
							data-test="newVendorRecord"
						/>
					</div>
				</template>
			</Modals>
		</template>
		<template #modal-footer>
			<ButtonGroup
				:items="[
					{
						id: `save-${uid}`,
						className: 'btn--primary',
						labelText: 'Save',
						type: 'submit',
						disabled: hasErrorModal,
						btnClick: fetchMatches
					},
					{
						id: `cancel-${uid}`,
						className: 'btn--link',
						labelText: 'Cancel',
						btnClick: cancelAction
					}
				]"
			/>
		</template>
	</Modals>
</template>

<script>
import ButtonGroup from '@/components/forms/button/button-group';
import Input from '@/components/forms/input/input';
import Modals from '@/components/shells/modal/modals';
import DatePicker from '@/components/forms/date-picker/date-picker';
import SegmentedControl from '@/components/forms/segmented-control/segmented-control.vue';
import List from '@/components/shells/list/list';
import Radiobutton from '@/components/forms/selection-control/radiobutton/radiobutton';
import { mapActions, mapGetters } from 'vuex';
import _ from 'lodash';
import {
	MODULE_NAME,
	SAVE_VENDOR,
	SET_MATCHES,
	MATCHES,
	VENDOR
} from '../vendors-mutation-types';

export default {
	name: 'NewVendor',
	components: {
		ButtonGroup,
		Input,
		Modals,
		DatePicker,
		SegmentedControl,
		List,
		Radiobutton
	},
	model: {
		prop: 'display',
		event: 'show'
	},
	props: {
		display: {
			type: Boolean,
			default: false
		}
	},
	data() {
		return {
			vendorModal: {
				active: false,
				dmsis: false
			},
			hasMatches: false, // true to test
			displayDuplicatesModal: false,
			matchIdSelection: -1,
			possiblesMatches: [],
			vendorContact: {},
			hasInvalidContactMethod: false,
			datePartnered: new Date(),
			is3PVendorISOptions: [
				{ value: true, text: 'Yes' },
				{ value: false, text: 'No' }
			],
			vendorStatusOptions: [
				{ value: true, text: 'Active' },
				{ value: false, text: 'Inactive' }
			]
		};
	},
	computed: {
		...mapGetters(MODULE_NAME, [MATCHES, VENDOR]),

		hasErrorModal() {
			const { integratorName } = this.vendorModal || {};
			const { name } = this.vendorContact || {};

			let hasError =
				_.isEmpty(integratorName) ||
				_.isEmpty(name) ||
				!this.hasAtleastOneContact;
			return hasError;
		},
		hasAtleastOneContact() {
			const { email, phone } = this.vendorContact;
			return !!(
				(email && !this.hasInvalidContactMethod) ||
				(phone && !this.hasInvalidContactMethod)
			);
		}
	},
	methods: {
		...mapActions(MODULE_NAME, {
			saveVendorAction: SAVE_VENDOR,
			getMatches: SET_MATCHES
		}),
		async fetchMatches() {
			const self = this;

			await self
				.getMatches({
					integratorName: self.vendorModal.integratorName,
					alias: self.vendorModal.alias
				})
				.then(res => {
					self.hasMatches = res;
				})
				.catch()
				.finally();
			if (self.hasMatches) {
				self.displayDuplicatesModal = true;
				return;
			}
			await self.saveAction();
		},
		async saveAction() {
			const self = this;
			let vendor = self.vendorModal;
			Object.assign(vendor, {
				Contact: self.vendorContact,
				dateActive: self.datePartnered
			});

			await self
				.saveVendorAction(vendor)
				.then(() => {
					const id = this[VENDOR].id;
					self.$router.push({
						name: 'vendors-settings',
						params: {
							id: id
						}
					});
				})
				.catch()
				.finally();
		},
		cancelAction() {
			this.vendorModal = {};
			this.vendorContact = {};
			this.$emit('show', false);
		},
		async continueBtn() {
			if (this.matchIdSelection === -1) {
				// create a new record
				await this.saveAction();
			} else {
				// push route to existing record
				this.$router.push({
					name: 'vendors-settings',
					params: {
						id: this.matchIdSelection
					}
				});
			}
			this.displayDuplicatesModal = false;
			this.display = false;
		},
		cancelSecondModal() {
			this.displayDuplicatesModal = false;
		},
		handleContactMethodError(obj) {
			this.hasInvalidContactMethod = obj['email'] || obj['phoneNumber'];
		},
		updateMatchIdSelection(val) {
			this.matchIdSelection = val;
		}
	},
	watch: {
		display: {
			handler(val) {
				if (val) {
					if (!this.validatePermission('vendors.create')) {
						this.closeModal();
						return;
					}
				}
			}
		},
		[MATCHES]: {
			deep: true,
			handler(val) {
				if (val.items?.length) {
					this.possiblesMatches = val.items;
					this.matchIdSelection = this.possiblesMatches[0].id;
				}
			}
		}
	}
};
</script>

<style lang="scss" scoped>
/**
    * [Description]
    *
    * Add a 3P Vendor Modal / Duplicates Vendor Modal
    * Can use SCSS variables = TRUE
    *
 */

.add-vendor {
	&__fields {
		display: grid;
		grid-template-columns: 1fr;
		gap: $gutter-xl;

		@include mq($from: sm-l) {
			grid-template-columns: 1fr 1fr;
		}

		@include mq($from: md) {
			width: 720px;
			padding-bottom: $padding-sm;
		}

		&-integration-solution {
			display: grid;
			grid-template-columns: 1fr;
			gap: $gutter-xl;

			@include mq($from: sm-l) {
				grid-column: 1 / 3;
				grid-template-columns: 1fr 1fr;
			}
		}

		&-support-contact-title {
			@include heading('md');

			@include mq($from: sm-l) {
				grid-column: 1 / 3;
			}
		}

		&-contact-method {
			&-title {
				@include heading('sm');
			}

			&-subtitle {
				@include body-text('sm-supporting');
			}

			@include mq($from: sm-l) {
				grid-column: 1 / 3;
			}
		}

		&-phone {
			@include mq($from: sm-l) {
				max-width: 240px;
			}
		}
	}
}

::v-deep .modal--confirmation {
	@include mq($from: sm-l) {
		width: 360px;
	}
}

.duplicates-vendor-modal {
	&__text {
		@include body-text('regular');
		padding-left: $padding-xs;
	}

	&__list {
		display: grid;
		grid-template-columns: 1fr;
		row-gap: $gutter-md;
		padding: 0 0 $padding-sm $padding-xs;
		@include scrollbar;
		max-height: 204px;
		overflow-y: auto;
		margin-top: $gutter-xl;
	}

	&__new-record {
		margin: $gutter-lg 0;
		padding-left: 16px;
	}
}
</style>
